import { useState, useEffect } from 'react';
import { AlertTriangle, Package, Shield, Wrench, RefreshCw, Clock, HelpCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  PageHeader,
  MetricCard,
  KPIGrid,
  LoadingState,
  DataTable,
  Column,
  TableFilters,
} from '@/components/common';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { inventoryApi, CriticalSpareRanking, CriticalSparesSummary } from '@/api/services';
import { cn } from '@/lib/utils';
import { motion } from 'framer-motion';
import { stagger, fadeUp } from '@/lib/animations';

const rankingColors: Record<CriticalSpareRanking['ranking'], string> = {
  CRITICAL: 'bg-danger-50 text-danger-500 border-danger-200',
  HIGH: 'bg-warning-50 text-warning-600 border-warning-200',
  MEDIUM: 'bg-primary-50 text-primary-500 border-primary-200',
  LOW: 'bg-muted text-muted-foreground border-border',
  // Dashed, not muted. An item nobody has judged is a decision outstanding,
  // not a low-priority one, and greying it out is how it stays outstanding.
  NOT_ASSESSED: 'border-dashed border-warning-400 text-warning-600',
};

const rankingIcons: Record<CriticalSpareRanking['ranking'], typeof AlertTriangle> = {
  CRITICAL: AlertTriangle,
  HIGH: Shield,
  MEDIUM: Package,
  LOW: Wrench,
  NOT_ASSESSED: HelpCircle,
};

const rankingLabels: Record<CriticalSpareRanking['ranking'], string> = {
  CRITICAL: 'CRITICAL',
  HIGH: 'HIGH',
  MEDIUM: 'MEDIUM',
  LOW: 'LOW',
  NOT_ASSESSED: 'NOT ASSESSED',
};

export default function CriticalSpares() {
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  // Tabs make the coarse cut — attention, everything, unjudged — so the search
  // box is for finding one part rather than for narrowing by ranking, which the
  // ranking Select here used to do twice over.
  const [tab, setTab] = useState('attention');
  const [search, setSearch] = useState('');

  const [summary, setSummary] = useState<CriticalSparesSummary | null>(null);
  const [spares, setSpares] = useState<CriticalSpareRanking[]>([]);

  // Load data
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [summaryData, sparesData] = await Promise.all([
        inventoryApi.getCriticalSparesSummary(),
        inventoryApi.getCriticalSpares(),
      ]);
      setSummary(summaryData);
      setSpares(sparesData);
    } catch (error) {
      console.error('Failed to load critical spares:', error);
      toast.error('Failed to load critical spares data');
    } finally {
      setLoading(false);
    }
  };

  const handleApplyRankings = async () => {
    try {
      setApplying(true);
      const result = await inventoryApi.applySparesRankings();
      toast.success(`Rankings applied to ${result.updated} spare parts`);
      if (result.errors.length > 0) {
        toast.warning(`${result.errors.length} items had errors`);
      }
      await loadData();
    } catch (error) {
      console.error('Failed to apply rankings:', error);
      toast.error('Failed to apply rankings');
    } finally {
      setApplying(false);
    }
  };

  if (loading) {
    return <LoadingState />;
  }

  // At risk of a stock-out, by item id, so a row can be marked and counted
  // without re-deriving the condition in three places.
  const atRisk = new Set((summary?.stockoutRisks ?? []).map((r) => r.itemId));

  const needsAttention = spares.filter(
    (s) => atRisk.has(s.itemId) || s.ranking === 'CRITICAL' || s.ranking === 'HIGH'
  );
  const notAssessed = spares.filter((s) => s.ranking === 'NOT_ASSESSED');

  const matchesSearch = (s: CriticalSpareRanking) =>
    !search ||
    s.itemName.toLowerCase().includes(search.toLowerCase()) ||
    s.itemSku.toLowerCase().includes(search.toLowerCase());

  // Lead time is the figure that decides whether a stock-out is recoverable, so
  // the worst one on the register is worth stating rather than leaving to be
  // found by sorting a column.
  const longestLead = spares.reduce<number | null>(
    (worst, s) =>
      worst == null || s.metrics.leadTimeDays > worst ? s.metrics.leadTimeDays : worst,
    null
  );

  const columns: Column<CriticalSpareRanking>[] = [
    {
      key: 'item',
      linkTo: (spare) => `/inventory/${spare.itemId}`,
      header: 'Spare Part',
      render: (spare) => (
        <div>
          <div className="font-medium">{spare.itemName}</div>
          <div className="font-mono text-sm text-muted-foreground">{spare.itemSku}</div>
        </div>
      ),
    },
    {
      key: 'ranking',
      header: 'Ranking',
      sortable: true,
      render: (spare) => {
        const Icon = rankingIcons[spare.ranking];
        return (
          <div className="flex flex-wrap items-center gap-1.5">
            <Badge variant="outline" className={cn('font-semibold', rankingColors[spare.ranking])}>
              <Icon className="mr-1 h-3 w-3" />
              {rankingLabels[spare.ranking]}
            </Badge>
            {atRisk.has(spare.itemId) && (
              <Badge
                variant="outline"
                className="bg-status-destructive-subtle text-status-destructive-foreground"
              >
                Below minimum
              </Badge>
            )}
          </div>
        );
      },
    },
    {
      key: 'criticalityScore',
      header: 'Score',
      sortable: true,
      className: 'text-right',
      accessorFn: (spare) => spare.criticalityScore ?? -1,
      render: (spare) => (
        <div className="text-right font-medium tabular-nums">
          {spare.criticalityScore != null ? (
            spare.criticalityScore.toFixed(1)
          ) : (
            // Null is unjudged, not nought. A zero here would sort it to the
            // bottom of a list whose whole job is deciding what to stock.
            <span className="text-sm font-normal text-status-warning-foreground">Not assessed</span>
          )}
        </div>
      ),
    },
    {
      key: 'consequence',
      header: 'What a stock-out costs',
      render: (spare) => (
        <div>
          <div className="font-medium">
            {spare.metrics.downtimeCostPerHour != null
              ? `$${spare.metrics.downtimeCostPerHour.toLocaleString()}/hr`
              : 'Not recorded'}
          </div>
          <div className="text-xs text-muted-foreground">
            {spare.linkedEquipment.length} {spare.linkedEquipment.length === 1 ? 'asset' : 'assets'}
            {spare.metrics.equipmentCriticality != null &&
              ` · criticality ${spare.metrics.equipmentCriticality.toFixed(1)}/10`}
          </div>
        </div>
      ),
    },
    {
      key: 'replacement',
      header: 'How long to replace',
      sortable: true,
      accessorFn: (spare) => spare.metrics.leadTimeDays,
      render: (spare) => (
        <div>
          <div className="font-medium tabular-nums">{spare.metrics.leadTimeDays} days</div>
          {spare.metrics.mtbf != null && (
            <div className="text-xs text-muted-foreground">
              fails every {spare.metrics.mtbf.toLocaleString()}h
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'stock',
      header: 'Recommended holding',
      render: (spare) => (
        <div>
          <div className="font-medium tabular-nums">
            {spare.stockRecommendation.recommendedMinStock} –{' '}
            {spare.stockRecommendation.recommendedMaxStock}
          </div>
          {spare.stockRecommendation.multiSourceRequired && (
            <Badge variant="outline" className="mt-1 text-xs">
              Multi-source required
            </Badge>
          )}
        </div>
      ),
    },
  ];

  const table = (rows: CriticalSpareRanking[], emptyTitle: string, emptyDescription: string) => (
    <DataTable<CriticalSpareRanking>
      data={rows.filter(matchesSearch)}
      columns={columns}
      toolbar={
        <TableFilters
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search by name or SKU…"
        />
      }
      pagination
      exportable
      columnToggle
      emptyTitle={emptyTitle}
      emptyDescription={emptyDescription}
    />
  );

  return (
    <motion.main className="space-y-5" variants={stagger} initial="hidden" animate="show">
      <motion.div variants={fadeUp}>
        {/* The criticality formula used to sit in a banner across the page. It
            is in the module's own explanation now, behind the ? beside the
            title, where every other screen keeps the same kind of thing. */}
        <PageHeader
          breadcrumbs={[
            { label: 'Home', href: '/' },
            { label: 'Inventory', href: '/inventory' },
            { label: 'Critical Spares' },
          ]}
          title="Critical Spares"
          description="Spare parts ranked by the cost of not holding them, with a recommended minimum and maximum."
          actions={
            <div className="flex gap-2">
              <Button variant="outline" onClick={loadData}>
                <RefreshCw className="mr-2 h-4 w-4" />
                Refresh
              </Button>
              <Button onClick={handleApplyRankings} disabled={applying}>
                {applying ? 'Applying…' : 'Apply rankings'}
              </Button>
            </div>
          }
        />
      </motion.div>

      <motion.div variants={fadeUp}>
        <KPIGrid columns={4}>
          <MetricCard
            label="Critical & high"
            value={spares.filter((s) => s.ranking === 'CRITICAL' || s.ranking === 'HIGH').length}
            icon={<AlertTriangle className="h-5 w-5" />}
            sentiment={
              spares.some((s) => s.ranking === 'CRITICAL' || s.ranking === 'HIGH')
                ? 'warning'
                : 'success'
            }
            subtitle="a stock-out here stops work"
          />
          <MetricCard
            label="Below minimum"
            value={atRisk.size}
            icon={<Package className="h-5 w-5" />}
            sentiment={atRisk.size > 0 ? 'danger' : 'success'}
            subtitle={atRisk.size > 0 ? 'short of the recommended holding' : 'none short'}
          />
          <MetricCard
            label="Not assessed"
            value={notAssessed.length}
            icon={<HelpCircle className="h-5 w-5" />}
            sentiment={notAssessed.length > 0 ? 'warning' : 'success'}
            subtitle="the consequence has not been judged"
          />
          <MetricCard
            label="Longest lead time"
            // The figure that decides whether a stock-out is recoverable at all.
            value={longestLead == null ? 'No parts' : longestLead}
            suffix={longestLead == null ? undefined : ' days'}
            icon={<Clock className="h-5 w-5" />}
            sentiment={longestLead != null && longestLead > 60 ? 'warning' : 'info'}
            subtitle="worst on the register"
          />
        </KPIGrid>
      </motion.div>

      <motion.div variants={fadeUp}>
        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <TabsList>
            <TabsTrigger value="attention">Needs attention ({needsAttention.length})</TabsTrigger>
            <TabsTrigger value="all">All spares ({spares.length})</TabsTrigger>
            <TabsTrigger value="unassessed">Not assessed ({notAssessed.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="attention" className="mt-6">
            {table(
              needsAttention,
              'Nothing needs attention',
              'No spare is ranked critical or high, and none is below its recommended minimum.'
            )}
          </TabsContent>

          <TabsContent value="all" className="mt-6">
            {table(
              spares,
              'No spares ranked yet',
              'Apply rankings to score the catalogue against equipment criticality and lead time.'
            )}
          </TabsContent>

          <TabsContent value="unassessed" className="mt-6">
            {table(
              notAssessed,
              'Every spare has been assessed',
              'Each part on the register has a recorded consequence of failure behind its score.'
            )}
          </TabsContent>
        </Tabs>
      </motion.div>
    </motion.main>
  );
}
